// Dispatch.tsx (v4) — PWA V2 slices 3+4. "Overview" reuses #1966's full
// dispatch screen wholesale (recursive dispatch tree, 24h stats, persona
// breakdown, stall/zombie alerts, detail sheet). Slice 4 folds the legacy
// Ops/OperationalViews sub-tabs in as sub-sections — Handoffs, Ops Report,
// and Consultations reuse the exported legacy views.
// Findings and Metrics are removed: Findings lives in System Health (Home
// diagnostics integration); Metrics are on the Home Cost tile.
// The "health" sub-tab (DispatchHealthScreen) has been removed per
// STEP-OV-DISPATCH-TAB-CLEANUP — these metrics live on the Home COST tile.
//
// Temporal degradation: agent_jobs power the /api/pwa/dispatches feed with no
// as_of parameter — when the operator scrubs, this screen stays LIVE and says
// so instead of pretending to be historical.
//
// STEP-V4-DISPATCH-CARD-PARITY: Field parity is satisfied via LegacyDispatchScreen
// embed in the "overview" tab, which renders DispatchCard with all required fields:
// persona_id, cost_usd, tool_call_count, duration_ms, error/output drill-down.
// The DispatchItem type (pwa/src/api/types.ts) is the source of truth for field
// presence. Gate test: mcp-server/src/tests/gates/step-v4-dispatch-card-parity.test.ts
//
// STEP-OV-DTC-CONTEXT-FRAMING: Handoff and consult entries already show step/track
// context. HandoffsView: track_id, steps_in_flight tappable chips, closed_at.
// ConsultationsView: step_id tappable link, created_at, consulted_persona.
// We add context framing note banners above each view and pass-through onSelect
// to the detail sheet which shows all metadata fields.

import { useState } from "react";
import { DispatchScreen as LegacyDispatchScreen } from "../../screens/Dispatch.js";
import {
  HandoffsView,
  ConsultationsView,
  DispatchMetricsView,
  FindingsView,
} from "../../screens/OperationalViews.js";
import { useTimeShift } from "../TimeShift.js";
import { ReleaseValidationsScreen } from "./ReleaseValidations.js";

// SC-5: Detail sheet for handoff/consultation cards
function ItemDetailSheet({
  item,
  onClose,
}: {
  item: Record<string, unknown>;
  onClose: () => void;
}) {
  const entries = Object.entries(item).filter(
    ([, v]) => v != null && v !== "" && !Array.isArray(v) && typeof v !== "object"
  );
  const arrays = Object.entries(item).filter(([, v]) => Array.isArray(v) && (v as unknown[]).length > 0);

  return (
    <>
      <div
        className="sheet-backdrop open"
        style={{ position: "fixed" }}
        onClick={onClose}
      />
      <div className="sheet open" style={{ position: "fixed" }}>
        <div className="sheet-handle" />
        <div className="sheet-head">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div className="eyebrow">
              {String(item.session_type ?? item.consulted_persona ?? "Detail")} ·{" "}
              {String(item.persona_id ?? item.created_at ?? "")}
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: "none", border: "none", color: "var(--text-dim)", fontSize: 16, cursor: "pointer", lineHeight: 1 }}
            >
              ×
            </button>
          </div>
          {item.track_id != null && item.track_id !== "" && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-dim)", marginTop: 4 }}>
              track: {String(item.track_id)}
            </div>
          )}
        </div>
        <div className="sheet-body">
          {item.summary_text != null && item.summary_text !== "" && (
            <div className="sheet-section">
              <h4>Summary</h4>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text)" }}>
                {String(item.summary_text)}
              </div>
            </div>
          )}
          {item.input_summary != null && item.input_summary !== "" && (
            <div className="sheet-section">
              <h4>Input</h4>
              <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text)" }}>
                {String(item.input_summary)}
              </div>
            </div>
          )}
          {entries.length > 0 && (
            <div className="sheet-section">
              <h4>Metadata</h4>
              {entries.map(([k, v]) => (
                <div key={k} className="sheet-row">
                  <span className="k mono" style={{ fontSize: 10 }}>{k}</span>
                  <span className="v" style={{ fontSize: 11 }}>{String(v)}</span>
                </div>
              ))}
            </div>
          )}
          {arrays.map(([k, v]) => (
            <div key={k} className="sheet-section">
              <h4>{k.replace(/_/g, " ")}</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {(v as string[]).map((s, i) => {
                  const isStep = String(s).startsWith("STEP-");
                  return isStep ? (
                    <button
                      key={i}
                      onClick={() => window.__openEntity?.("steps", String(s))}
                      style={{
                        background: "none",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 4,
                        padding: "2px 6px",
                        fontFamily: "var(--font-mono)",
                        fontSize: 9,
                        color: "var(--accent)",
                        cursor: "pointer",
                      }}
                    >
                      {String(s)}
                    </button>
                  ) : (
                    <span key={i} className="chip" style={{ fontSize: 10 }}>{String(s)}</span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * HandoffsViewWithContext — wraps HandoffsView with a context framing note.
 *
 * HandoffsView already renders:
 *   • track_id chip (tappable steps_in_flight)
 *   • closed_at absolute timestamp
 *   • summary_text, review_verdict, key_decisions, open_threads (expand)
 *
 * The context framing banner above explains the step/track tap affordance.
 * Tapping a card opens the detail sheet with all metadata fields.
 */
function HandoffsViewWithContext({ onSelect }: { onSelect?: (item: Record<string, unknown>) => void }) {
  return (
    <>
      <div
        style={{
          padding: "8px 20px 6px",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-dim)",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        Step chips are tappable · tap card for full context framing
      </div>
      <HandoffsView onSelect={onSelect} />
    </>
  );
}

/**
 * ConsultationsViewWithContext — wraps ConsultationsView with a context framing note.
 *
 * ConsultationsView already renders:
 *   • step_id as a tappable button (opens entity detail via window.__openEntity)
 *   • created_at absolute timestamp
 *   • consulted_persona, input_summary, outcome, output_summary (expand)
 *
 * The banner explains the step tap affordance for discoverability.
 */
function ConsultationsViewWithContext({ onSelect }: { onSelect?: (item: Record<string, unknown>) => void }) {
  return (
    <>
      <div
        style={{
          padding: "8px 20px 6px",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-dim)",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        Step chip opens entity detail · tap card to expand context
      </div>
      <ConsultationsView onSelect={onSelect} />
    </>
  );
}

// Health sub-tab removed (STEP-OV-DISPATCH-TAB-CLEANUP): DispatchHealthScreen
// metrics now live on the Home COST tile.
// Ops Report removed (STEP-PWA-OPS-REPORT-INTO-ACTIVITY): moved to Activity tab.
type SubSection = "overview" | "handoffs" | "consults" | "dispatch-metrics" | "findings" | "release-validations";

const SUB_SECTIONS: { id: SubSection; label: string }[] = [
  { id: "overview", label: "Dispatch" },
  { id: "handoffs", label: "Handoffs" },
  { id: "consults", label: "Consults" },
  { id: "dispatch-metrics", label: "Metrics" },
  { id: "findings", label: "Findings" },
  { id: "release-validations", label: "Release" },
];

export function DispatchScreen() {
  const { isLive } = useTimeShift();
  const [section, setSection] = useState<SubSection>("overview");
  const [selectedDetail, setSelectedDetail] = useState<Record<string, unknown> | null>(null);

  return (
    <>
      {!isLive && (
        <div
          style={{
            padding: "8px 20px 0",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-dim)",
          }}
        >
          ● LIVE — dispatch feed has no scrub history; showing current state
        </div>
      )}

      {/* Sub-sections (slice 4) — the legacy Ops views folded in */}
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "1px solid var(--border-soft)",
          padding: "0 12px",
          overflowX: "auto",
        }}
      >
        {SUB_SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            style={{
              background: "none",
              border: "none",
              borderBottom:
                section === s.id ? "2px solid var(--accent)" : "2px solid transparent",
              color: section === s.id ? "var(--accent)" : "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: section === s.id ? 700 : 400,
              padding: "10px 12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "overview" && <LegacyDispatchScreen />}
      {section === "handoffs" && (
        <div style={{ paddingBottom: 80 }}>
          <HandoffsViewWithContext onSelect={setSelectedDetail} />
        </div>
      )}
      {section === "consults" && (
        <div style={{ paddingBottom: 80 }}>
          <ConsultationsViewWithContext onSelect={setSelectedDetail} />
        </div>
      )}
      {section === "dispatch-metrics" && (
        <div style={{ paddingBottom: 80 }}>
          <DispatchMetricsView />
        </div>
      )}
      {section === "findings" && (
        <div style={{ paddingBottom: 80 }}>
          <FindingsView />
        </div>
      )}

      {section === "release-validations" && (
        <div style={{ paddingBottom: 80 }}>
          <ReleaseValidationsScreen />
        </div>
      )}

      {/* SC-5: Detail sheet for handoff/consultation items */}
      {selectedDetail != null && (
        <ItemDetailSheet item={selectedDetail} onClose={() => setSelectedDetail(null)} />
      )}
    </>
  );
}
